import React, { Component } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import i18n from "../../i18n";
import * as rk from "../../localization/resourceKeys";
import VerificationForm from "./verificationForm";
import { getPortalVerificationDetails } from "../../redux/VerificationForm/verificationAction";
import { secureKeyGetPortalVerificationDetails } from "../../redux/SecureKey/secureKeyActions";
import ReCAPTCHA from "react-google-recaptcha";
import { siteKey } from "../../components/ReCaptcha/reCaptcha";
import moment from "moment";
import { clearErrors } from "../../redux/Errors/actions";
import "./verificationForm.css";
import BottomLine from "../Footer/BottomLine";
import { resetStatusReducer } from "../../redux/StatusForm/statusActions";
import { getProtectionServicesExperianDetailsCheckMyRequests } from "../../redux/ProtectionServicesExperianForm/protectionServicesExperianAction";
import {
  texasLikeStates,
  utahLikeStates,
  virginiaLikeStates,
} from "../../utils/statesUS";

const recaptchaRef = React.createRef();

export class GetVerificationForm extends Component {
  constructor(props) {
    super(props);

    const { selectedRequest, stateSelector } = this.props;
    const areProtectionServices =
      stateSelector?.brandAffiliation === i18n.t(rk.BRANDS_OPTION_VWD_APP_DER);

    if (!selectedRequest && !areProtectionServices) {
      window.location.replace("/");
    }
    this.state = {
      captchaEnabled: !(
        process.env.REACT_APP_AWS_ENV === "DEVELOPMENT" ||
        window.location.origin.includes("localhost")
      ),
      captcha: false,
      isVWGoAConfirmationNumber: false,
      isVWGoAPrefixDetected: false,
      hasSubmitted: false,
    };
    this.props.resetStatusFormReducer();
    this.props.clearErrors();
  }

  getInitialValues() {
    const { experianReducer } = this.props;
    const initialValues = {
      firstName: "",
      lastName: `${this.props.secureKey?.familyName || ""}`,
      dateOfBirth: `${this.props.secureKey?.dateOfBirth || ""}`,
      last4Ssn: "",
      ssn: "",
      confirmationNumber: "",
      pinNumber: "",
      reportType: "",
      addressLine1: "",
      city: "",
      state: "",
      postalCode: "",
      vin: "",
    };

    // Set initial values when user wants to retry experian request.
    if (
      experianReducer?.payload &&
      this.props.history?.location?.state?.isRequestConfirmation !== true
    ) {
      const { payload } = experianReducer;
      initialValues.firstName = payload.firstName;
      initialValues.lastName = payload.lastName;
      initialValues.dateOfBirth = payload.dateOfBirth;
      initialValues.ssn = payload.fullSsn;
      initialValues.last4Ssn = payload.last4Ssn;
      initialValues.confirmationNumber = payload.confirmationNumber;
      initialValues.pinNumber = payload.pin;
      initialValues.reportType = payload.reportType;
      initialValues.addressLine1 = payload.addressLine1;
      initialValues.city = payload.city;
      initialValues.state = payload.stateOrProvince;
      initialValues.postalCode = payload.postalCode;
      initialValues.vin = payload.vin;
    }

    return initialValues;
  }

  doNotShowDoB() {
    return (
      this.props.selectedRequest ===
        i18n.t(rk.REQUEST_SELECTOR.DO_NOT_SELL_INFO) ||
      this.isDeleteMyInfo() ||
      this.state.isVWGoAConfirmationNumber
    );
  }

  doNotShowSSN() {
    return (
      this.props.selectedRequest ===
        i18n.t(rk.REQUEST_SELECTOR.DO_NOT_SELL_INFO) ||
      this.isDeleteMyInfo() ||
      this.props.country.toLowerCase() === "canada" ||
      this.state.isVWGoAConfirmationNumber
    );
  }
  setIsVWGoAConfirmationNumber = (
    isVWGoAConfirmationNumber,
    isVWGoAPrefixDetected,
  ) => {
    this.setState({
      isVWGoAConfirmationNumber,
      isVWGoAPrefixDetected,
    });
  };

  // This function was created to assist in validating the Date of Birth field because of issues run into when testing in older
  // browsers (IE11 and Safari, specifically). The issue is that older browsers do not recognize HTML inputs of type="date" which is
  // what is being used for this field. In browsers that do not recognize this HTML input, the value returned is a string. In browsers
  // that do recognize the HTML input, the value is returned in a date format based on ISO8601 (YYYY-MM-DD). This function checks for
  // the presence of "/" to determine which format and then validates based on that.
  birthDateValidation(inputValue, minBirthDate, maxBirthDate) {
    let returnValue = false;
    let dateObject = null;

    if (
      inputValue !== null &&
      inputValue !== undefined &&
      inputValue.length > 0
    ) {
      if (inputValue.indexOf("/") !== -1) {
        //this date format is a string of "MM/DD/YYYY" - validate with that format in mind. Note: the "L" here is MomentJs shorthand for "MM/DD/YYYY"
        dateObject = moment(inputValue, "L", true);
        if (!(dateObject > new Date())) {
          //when min & max are passed, validate validity and min/max restrictions. Otherwise just validity
          returnValue =
            minBirthDate && maxBirthDate
              ? dateObject.isValid() &&
                moment(dateObject).isSameOrAfter(minBirthDate) &&
                moment(dateObject).isSameOrBefore(maxBirthDate)
              : dateObject.isValid();
        }
      } else {
        //this date format adheres to ISO8601 (YYYY-MM-DD) - validate with that format in mind. Note: the "L" here is MomentJs shorthand for "MM/DD/YYYY"
        dateObject = moment(inputValue);
        if (dateObject.isValid()) {
          //when min & max are passed, validate validity and min/max restrictions. Otherwise just validity
          returnValue =
            minBirthDate && maxBirthDate
              ? moment(dateObject.format("L"), "L", true).isValid() &&
                moment(dateObject).isSameOrAfter(minBirthDate) &&
                moment(dateObject).isSameOrBefore(maxBirthDate)
              : moment(dateObject.format("L"), "L", true).isValid();
        }
      }
    }
    return returnValue;
  }

  isDeleteMyInfo = () => {
    const validStates = [...virginiaLikeStates, ...utahLikeStates];

    const currentStateResidence =
      this.props.stateSelector.currentStateResidence;

    return (
      validStates.includes(currentStateResidence) &&
      this.props.selectedRequest === i18n.t(rk.REQUEST_SELECTOR.DELETE_INFO)
    );
  };

  validate() {
    const { t } = this.props;
    const isQuebec = this.props.country.toLowerCase() === "canada";
    const areProtectionServices =
      this.props?.stateSelector?.brandAffiliation ===
      t(rk.BRANDS_OPTION_VWD_APP_DER);
    const isVWGoAPrefixDetected = this.state.isVWGoAPrefixDetected;
    const postalCodeValidation = /^\d{5}(?:-\d{4})?$/;
    const minBirthDate = moment("0001-01-01");
    const maxBirthDate = moment(new Date()).add(-18, "years");

    let returnValue;

    if (!areProtectionServices) {
      // VWGoA confirmationNumber format is different (e.g. VWGOA_AGJY0N3L)
      // VCI confirmationNumber format example: AGJY0N3L
      returnValue = Yup.object().shape({
        lastName: Yup.string()
          .required(
            t(rk.CCPA_FORM_ERROR.LAST_NAME_ERROR, { fieldName: "Last Name" }),
          )
          .max(
            40,
            t(rk.CCPA_FORM_ERROR.LAST_NAME_ERROR, { fieldName: "Last Name" }),
          ),
        confirmationNumber: Yup.string()
          .required(t(rk.CCPA_FORM_ERROR.CONFIRMATION_NUM_ERROR))
          .test(
            "confirmation-number-length",
            isVWGoAPrefixDetected
              ? t(rk.CCPA_FORM_ERROR.CONFIRMATION_NUM_ERROR_VWGOA)
              : t(rk.CCPA_FORM_ERROR.CONFIRMATION_NUM_ERROR),
            (value = "") => {
              if (isVWGoAPrefixDetected) {
                return value.length === 14; // VWGOA_XXXXXXXX
              }
              return value.length === 8; // VWFS
            },
          ),
        pinNumber: Yup.string()
          .min(4, t(rk.CCPA_FORM_ERROR.PIN_NUM_ERROR))
          .max(4, t(rk.CCPA_FORM_ERROR.PIN_NUM_ERROR)),
      });
    } else {
      returnValue = Yup.object().shape({
        lastName: Yup.string()
          .required(
            t(rk.CCPA_FORM_ERROR.LAST_NAME_ERROR, { fieldName: "Last Name" }),
          )
          .max(
            40,
            t(rk.CCPA_FORM_ERROR.LAST_NAME_ERROR, { fieldName: "Last Name" }),
          ),
        firstName: Yup.string()
          .required(
            t(rk.CCPA_FORM_ERROR.FIRST_NAME_ERROR, { fieldName: "First Name" }),
          )
          .matches(
            /^[A-Za-z0-9'&,ÀÁÂÄÇÈÉÊËÌÍÎÏÒÓÔÖàáâäçèéêëìíîïòóôöùúûüÙÚÛÜñÑ. _-]*$/,
            { message: t(rk.CCPA_FORM_ERROR.FIRST_NAME_ERROR) },
          )
          .max(40, t(rk.CCPA_FORM_ERROR.FIRST_NAME_ERROR)),
        ssn: Yup.string()
          .required(t(rk.CCPA_FORM_ERROR.FULL_SSN_ERROR))
          .matches(/^\d*$/, { message: t(rk.CCPA_FORM_ERROR.FULL_SSN_ERROR) })
          .min(9, t(rk.CCPA_FORM_ERROR.FULL_SSN_ERROR))
          .max(9, t(rk.CCPA_FORM_ERROR.FULL_SSN_ERROR)),
        addressLine1: Yup.string().required(t(rk.ERROR_REQUIRED)),
        city: Yup.string()
          .required(t(rk.CCPA_FORM_ERROR.CITY_ERROR))
          .matches(
            /^[A-Za-z0-9'&,ÀÁÂÄÇÈÉÊËÌÍÎÏÒÓÔÖàáâäçèéêëìíîïòóôöùúûüÙÚÛÜñÑ. _-]*$/,
            { message: t(rk.CCPA_FORM_ERROR.CITY_ERROR) },
          )
          .max(40, t(rk.CCPA_FORM_ERROR.CITY_ERROR)),
        state: Yup.string().required(t(rk.ERROR_REQUIRED)),
        postalCode: Yup.string()
          .required(t(rk.CCPA_FORM_ERROR.ZIP_CODE_ERROR))
          .matches(postalCodeValidation, t(rk.CCPA_FORM_ERROR.ZIP_CODE_ERROR))
          .max(5, t(rk.CCPA_FORM_ERROR.ZIP_CODE_ERROR)),
        vin: Yup.string()
          .required(t(rk.CCPA_FORM_ERROR.VIN_NUMBER_ERROR))
          .min(17, t(rk.CCPA_FORM_ERROR.VIN_NUMBER_ERROR))
          .max(17, t(rk.CCPA_FORM_ERROR.VIN_NUMBER_ERROR)),
      });
    }

    if (!this.doNotShowDoB()) {
      returnValue = returnValue.concat(
        Yup.object().shape({
          dateOfBirth: Yup.string()
            .required(t(rk.CCPA_FORM_ERROR.DOB_ERROR))
            .test("dateOfBirth", t(rk.INFO_FORM.DATE_REQUIRED), (value) => {
              return this.birthDateValidation(value);
            })
            .test(
              "dateOfBirth",
              t(rk.INFO_FORM.DATE_AGE_RESTRICTION),
              (value) => {
                return this.birthDateValidation(
                  value,
                  minBirthDate,
                  maxBirthDate,
                );
              },
            ),
        }),
      );
    }

    if (!this.doNotShowSSN() && !areProtectionServices) {
      returnValue = returnValue.concat(
        Yup.object().shape({
          last4Ssn: Yup.string()
            .required(t(rk.CCPA_FORM_ERROR.LAST4_SSN_ERROR))
            .matches(/^\d*$/, {
              message: t(rk.CCPA_FORM_ERROR.LAST4_SSN_ERROR),
            })
            .min(4, t(rk.CCPA_FORM_ERROR.LAST4_SSN_ERROR))
            .max(4, t(rk.CCPA_FORM_ERROR.LAST4_SSN_ERROR)),
        }),
      );
    }

    if (
      this.props.selectedRequest === t(rk.REQUEST_SELECTOR.ACCESS_INFO) &&
      !isQuebec &&
      !areProtectionServices
    ) {
      returnValue = returnValue.concat(
        Yup.object().shape({
          reportType: Yup.string().required(t(rk.ERROR_REQUIRED)),
        }),
      );
    }

    return returnValue;
  }

  onChange = () => {
    this.setState({
      captcha: true,
    });
  };

  onSubmit = () => {
    const recaptchaValue = recaptchaRef.current.getValue();
    this.props.onSubmit(recaptchaValue);
  };

  handleSubmit(values) {
    this.handleGetPortalVerificationDetails(values, this.props);
  }

  handleGetPortalVerificationDetails(values, props) {
    if (
      this.props.country?.toLowerCase() === "canada" &&
      this.getRequestType(values) === "TIER2_RPT"
    ) {
      const payload = {
        ...values,
        canadaVerification: {
          secureKeyId:
            this.props.secureKey?.comSecurekeyVerifiedMeLicenseId ||
            this.props.secureKey?.comSecurekeyVidsJobId ||
            "",
        },
      };

      this.props.secureKeyGetPortalVerificationDetails(payload, props);
    } else {
      !this.state.isVWGoAConfirmationNumber &&
        this.props.getPortalVerificationDetails(
          values,
          props,
          this.state.isVWGoAConfirmationNumber,
        );
    }
  }
  resetHasSubmitted = () => {
    this.setState({ hasSubmitted: false });
  };
  canSubmit = (values, t) => {
    const isKnowMyInfo =
      this.props.selectedRequest === t(rk.REQUEST_SELECTOR.ACCESS_INFO);
    const areProtectionServices =
      this.props?.stateSelector?.brandAffiliation ===
      t(rk.BRANDS_OPTION_VWD_APP_DER);
    const getCaptchaStatus = () => {
      const { captchaEnabled, captcha } = this.state;
      return (captchaEnabled && captcha) || !captchaEnabled;
    };
    const isCaptchaEnabled = getCaptchaStatus();

    if (areProtectionServices) {
      return (
        values.firstName &&
        values.lastName &&
        values.vin &&
        values.dateOfBirth &&
        values.ssn &&
        (this.props?.stateSelector?.currentStateResidence === "CA"
          ? values.reportType
          : true) &&
        isCaptchaEnabled
      );
    }

    return (
      values.lastName &&
      values.confirmationNumber &&
      (!this.doNotShowDoB() ? values.dateOfBirth : true) &&
      (!this.doNotShowSSN() ? values.last4Ssn : true) &&
      (isKnowMyInfo ? values.reportType : true) &&
      isCaptchaEnabled
    );
  };

  getRequestType = (values) => {
    const { t } = this.props;
    const areProtectionServices =
      this.props?.stateSelector?.brandAffiliation ===
      t(rk.BRANDS_OPTION_VWD_APP_DER);
    let requestType;
    if (
      this.props.selectedRequest === t(rk.REQUEST_SELECTOR.DO_NOT_SELL_INFO)
    ) {
      requestType = "DO_NOT_SELL";
    }
    if (this.props.selectedRequest === t(rk.REQUEST_SELECTOR.DO_NOT_USE_INFO)) {
      requestType = "DO_NOT_USE";
    }
    if (this.props.selectedRequest === t(rk.REQUEST_SELECTOR.DELETE_INFO)) {
      requestType = "DELETE";
    }
    if (
      (this.props.selectedRequest === t(rk.REQUEST_SELECTOR.ACCESS_INFO) &&
        values.reportType === t(rk.INFO_FORM.REPORT_CATEGORY_CARD_HEADER)) ||
      this.props.country?.toLowerCase() === "canada"
    ) {
      requestType = "TIER1_RPT";
    }
    if (
      this.props.selectedRequest === t(rk.REQUEST_SELECTOR.ACCESS_INFO) &&
      values.reportType === t(rk.INFO_FORM.REPORT_SPECIFIC_PIECES)
    ) {
      requestType = "TIER2_RPT";
    }
    if (this.props.selectedRequest === t(rk.REQUEST_SELECTOR.CORRECT_MY_DATA)) {
      requestType = "CORRECT_DATA";
    }
    if (this.props.selectedRequest === t(rk.REQUEST_SELECTOR.LIMIT_DATA)) {
      requestType = "LIMIT_DATA";
    }
    if (areProtectionServices) {
      if (
        values.reportType === t(rk.INFO_FORM.REPORT_CATEGORY_CARD_HEADER) &&
        this.props?.stateSelector?.currentStateResidence === "CA"
      ) {
        requestType = "TIER1_RPT";
      } else if (
        values.reportType === t(rk.INFO_FORM.REPORT_SPECIFIC_PIECES) &&
        this.props?.stateSelector?.currentStateResidence === "CA"
      ) {
        requestType = "TIER2_RPT";
      } else if (
        texasLikeStates.includes(
          this.props?.stateSelector?.currentStateResidence,
        )
      ) {
        requestType = "TIER2_RPT";
      }
    }
    return requestType;
  };

  getRequestorType = () => {
    const { t } = this.props;
    let requestorType = "Consumer";
    if (this.props.stateSelector.customerRelation === t(rk.QUESTION_2_OPTION_1))
      requestorType = "Consumer";
    if (this.props.stateSelector.customerRelation === t(rk.QUESTION_2_OPTION_2))
      requestorType = "Personnel";
    if (this.props.stateSelector.customerRelation === t(rk.QUESTION_2_OPTION_3))
      requestorType = "Business_Contact";
    return requestorType;
  };

  render() {
    const { t } = this.props;
    const initialValues = this.getInitialValues();
    return (
      <>
        <div>
          <Formik
            initialValues={initialValues}
            onSubmit={(values, { setSubmitting }) => {
              const areProtectionServices =
                this.props?.stateSelector?.brandAffiliation ===
                t(rk.BRANDS_OPTION_VWD_APP_DER);
              const stateOfResidence = this.props.stateSelector
                ?.currentStateResidence
                ? this.props.stateSelector?.currentStateResidence
                : this.props.country?.toLowerCase() === "canada"
                  ? "QC"
                  : "Other";
              const pinValidationPayload = {
                lastName: values.lastName.trim(),
                last4Ssn: values.last4Ssn || "",
                fullSsn: areProtectionServices ? values.ssn : "" || "",
                confirmationNumber: values.confirmationNumber,
                dateOfBirth:
                  values.dateOfBirth && !this.doNotShowDoB()
                    ? moment(values.dateOfBirth).format("MM/DD/YYYY")
                    : "",
                pin: values.pinNumber || "",
                requestType: this.getRequestType(values) || "",
                requestorType: this.getRequestorType(),
                stateOfResidence: stateOfResidence,
                vin: values.vin,
                addressLine1: values.addressLine1,
                city: values.city,
                reportType: values.reportType,
                postalCode: values.postalCode,
                firstName: values.firstName,
                stateOrProvince: values.state,
              };

              if (areProtectionServices) {
                this.props.getProtectionServicesExperianDetailsCheckMyRequests(
                  pinValidationPayload,
                  this.props.history,
                );
              } else {
                this.handleGetPortalVerificationDetails(
                  pinValidationPayload,
                  this.props,
                );
              }

              setSubmitting(false);
            }}
            validationSchema={this.validate()}
          >
            {(props) => {
              const { values, handleSubmit } = props;
              return (
                <div
                  onSubmit={props.handleSubmit}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      props.handleSubmit();
                    }
                  }}
                >
                  <div className="o-layout">
                    <div className="o-layout__item">
                      <VerificationForm
                        {...props}
                        networkError={this.props.networkError}
                        isDNS={
                          this.props.selectedRequest ===
                          t(rk.REQUEST_SELECTOR.DO_NOT_SELL_INFO)
                        }
                        isKnowMyInfo={
                          this.props.selectedRequest ===
                          t(rk.REQUEST_SELECTOR.ACCESS_INFO)
                        }
                        isDeleteMyInfo={this.isDeleteMyInfo(t)}
                        doNotShowDoB={this.doNotShowDoB()}
                        doNotShowSSN={this.doNotShowSSN()}
                        requestType={this.getRequestType(values)}
                        setIsVWGoAConfirmationNumber={
                          this.setIsVWGoAConfirmationNumber
                        }
                        initialValues={initialValues}
                        hasSubmitted={this.state.hasSubmitted}
                        showVWGoABanner={
                          this.state.hasSubmitted &&
                          this.state.isVWGoAConfirmationNumber
                        }
                        resetHasSubmitted={this.resetHasSubmitted}
                      />
                    </div>
                  </div>
                  {this.state.captchaEnabled === true && (
                    <div className="u-pt">
                      <div
                        data-test-id="recaptcha"
                        className="center-div"
                        style={{ marginTop: "30px" }}
                      >
                        <form onSubmit={this.onSubmit}>
                          <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={siteKey}
                            onChange={this.onChange}
                            hl={
                              this.props.language?.toUpperCase() === "FRA"
                                ? "fr"
                                : "en"
                            }
                            key={
                              this.props.language?.toUpperCase() === "FRA"
                                ? "fr"
                                : "en"
                            }
                          />
                        </form>
                      </div>
                    </div>
                  )}
                  <div className="o-layout u-pt">
                    <div
                      className="o-layout__item"
                      style={{ marginTop: "15px" }}
                    >
                      <button
                        data-testid="verification-form-btn"
                        className="c-btn u-block u-block-center"
                        onClick={async () => {
                          await this.props.clearErrors();
                          this.setState({ hasSubmitted: true });
                          handleSubmit();
                        }}
                        disabled={!this.canSubmit(values, t)}
                      >
                        <span className="c-btn__text">
                          {t(rk.BUTTON.SUBMIT)}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            }}
          </Formik>
        </div>
        <div className="request-footer-wrapper">
          <div id="request-footer-content" className="request-footer-content-2">
            <div id="request-footer-text">
              {i18n.t(rk.VERFICIATION_FORM.NOTE2)}
            </div>
            <BottomLine />
          </div>
        </div>
      </>
    );
  }
}

GetVerificationForm.propTypes = {
  getPortalVerificationDetails: PropTypes.func.isRequired,
  secureKeyGetPortalVerificationDetails: PropTypes.func.isRequired,
  getProtectionServicesExperianDetailsCheckMyRequests:
    PropTypes.func.isRequired,
  resetStatusFormReducer: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired,
  country: PropTypes.string,
  stateSelector: PropTypes.object,
  selectedRequest: PropTypes.string,
  experianReducer: PropTypes.object,
  info: PropTypes.object,
  networkError: PropTypes.bool,
  secureKey: PropTypes.object,
  history: PropTypes.object,
  onSubmit: PropTypes.func,
  t: PropTypes.func,
  language: PropTypes.string,
};

const mapStateToProps = (state) => ({
  country: state.initialReducer.country,
  language: state.initialReducer.language,
  stateSelector: state.stateSelector,
  selectedRequest: state.requestSelector.selectedRequest,
  experianReducer: state.getProtectionServicesExperianReducer,
  info: state.info,
  networkError: state.networkError,
  secureKey: state.secureKey,
});

const mapDispatchToProps = (dispatch) => ({
  getPortalVerificationDetails: (actions, props, isVWGoAConfirmationNumber) =>
    dispatch(
      getPortalVerificationDetails(actions, props, isVWGoAConfirmationNumber),
    ),
  secureKeyGetPortalVerificationDetails: (actions, props) =>
    dispatch(secureKeyGetPortalVerificationDetails(actions, props)),
  getProtectionServicesExperianDetailsCheckMyRequests: (action, routeProps) =>
    dispatch(
      getProtectionServicesExperianDetailsCheckMyRequests(action, routeProps),
    ),
  resetStatusFormReducer: () => dispatch(resetStatusReducer()),
  clearErrors: () => dispatch(clearErrors()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(GetVerificationForm));
